import { useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useRole } from "@/hooks/use-role";
import { useToast } from "@/hooks/use-toast";
import { useQaWebSocket } from "@/hooks/use-websocket";
import { shouldRaiseEarlyRiskToast } from "@/lib/active-helpers";

// SPEC-ActiveTask Phase B2 / Surface 4 — global in-app notification for an
// early-delivery risk. Mounted once at the authenticated app root so EVERY
// logged-in user gets the toast even when not looking at the Active Tasks tab.
// Auto-dismiss toast (the durable record is the Slack alert, B1). Click-through
// opens the task's Active detail. The backend dedupes via earlyRiskAlertedAt,
// so a given task fires the event at most once; we also guard locally.
export function EarlyRiskNotifier() {
  const { user } = useAuth();
  const { role } = useRole();
  // SPEC-TrainingSimulator §8.5 — a trainee never opens a socket.
  const isTrainee = role === "trainee";
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const seen = useRef<Set<string>>(new Set());

  const handleQaUpdate = useCallback(
    (event: {
      type: string;
      delivery?: { id?: string; recipientName?: string | null; workerName?: string | null };
      earlyRisk?: { minutesEarly: number | null } | null;
    }) => {
      const id = event.delivery?.id;
      if (!shouldRaiseEarlyRiskToast({ eventType: event.type, deliveryId: id, seen: seen.current })) return;
      seen.current.add(id!);

      const mins = event.earlyRisk?.minutesEarly;
      const driver = event.delivery?.workerName ?? "A driver";
      const recipient = event.delivery?.recipientName ?? "a CookUnity order";
      toast({
        title: "⏳ Early delivery risk",
        description: `${driver} may complete ${recipient}${mins != null ? ` ~${mins} min` : ""} before its window. Do not complete yet.`,
        // Click-through opens the active detail surface.
        onClick: () => navigate(`/qa/active/${id}`),
      } as any);
    },
    [toast, navigate],
  );

  useQaWebSocket(isTrainee ? null : (user?.id ?? null), handleQaUpdate, {
    userName: (user as any)?.firstName || undefined,
    email: user?.email || undefined,
  });

  return null;
}
